import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { downloadBlobGet, fetchTextGet, tabulatorGet } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import { DateTime } from 'luxon';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import { getEffectiveMask } from '../../utils/effectiveMask';
import { cleanSorters, createDedupedAjaxRequest, extractSafePaginationParams, mergePagedStandardListFiltersAndSorters, refreshProgressiveFromStart, safeParse, applyUserMaskRowToTabulator } from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import { resetTabulatorMask } from '../../utils/tabulatorResetMask';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../utils/tabulatorColumnMinWidth';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/sk';
import 'i18n-iso-countries/langs/en';
import 'i18n-iso-countries/langs/de';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import { EditIcon, PrintIcon, ExportIcon, RefreshIcon, VerticalLineIcon, EditRowIcon, FavoriteIcon, PriceIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, SearchIcon } from '../svg/svgIcons';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';
const CUSTOMER_MY_CUSTOMERS_LIST_COLUMN_I18N_KEYS = Object.freeze({
  'id': 'ServiceTaskTable.Col.Id',
  'name': 'CustomerMyCustomersList.Col.Name',
  'customer_state': 'CustomerCentralDatabaseList.Col.State',
  'customer_type': 'CustomerMyCustomersList.Col.CustomerClientType',
  'party_mode': 'CustomerCentralDatabaseList.Col.PartnerType',
  'defined_id': 'CustomerCentralDatabaseList.Col.DefinedId',
  'country': 'BusinessPartnerList.Column.Country',
  'address': 'BusinessPartnerList.Column.Address',
  'email': 'BusinessPartnerList.Column.Email',
  'trader_name': 'CustomerCentralDatabaseList.Col.Trader',
  'interested_users': 'CustomerMyCustomersList.Col.InterestedUsers',
  'waiting_for_action_by': 'ServiceTaskTable.Col.Deadline',
  'created_at': 'CustomerStatesList.Col.Created',
  'updated_at': 'ServiceTaskTable.Col.Updated',
  'deadline_status': 'ServiceTaskTable.Col.Id',
});



const DEFAULT_COLUMNS_VISIBLE = Object.freeze({
  deadline_status: true,
  id: true,
  name: true,
  customer_state: true,
  trader_name: true,
  interested_users: true,
  waiting_for_action_by: true,
  party_mode: true,
  customer_type: false,
  defined_id: true,
  country: true,
  address: true,
  email: true,
  created_at: true,
  updated_at: true,
});

const PAGE_LIST_KEY = 'CustomerMyCustomersList';
const PAGE_ADD_KEY = 'CustomerMyCustomersDetail';
const MASK_ADD = 'cust014';
countries.registerLocale(require('i18n-iso-countries/langs/sk'));
countries.registerLocale(require('i18n-iso-countries/langs/en'));
countries.registerLocale(require('i18n-iso-countries/langs/de'));

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE };
  return merged;
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE)) {
    if (visibilityMap[f] === false) table.hideColumn?.(f);
    else table.showColumn?.(f);
  }
  setTimeout(() => {
    table.redraw?.(true);
    table.modules?.layout?.layout?.();
  }, 0);
}

function typeLabelFromRow(row, t) {
  const mode = String(row?.party_mode || '').trim();
  const key = String(
    mode === 'client'
      ? (row?.client_type || row?.customer_type || '')
      : mode === 'customer'
        ? (row?.customer_type || row?.client_type || '')
        : (row?.customer_type || row?.client_type || '')
  ).trim();
  if (!key) return '';
  const labels = {
    new_client: t('CustomerTypeOptions.NewClient'),
    potential_client: t('CustomerTypeOptions.PotentialClient'),
    verified_client: t('CustomerTypeOptions.VerifiedClient'),
    inquiring_client: t('CustomerTypeOptions.InquiringClient'),
    occasional_customer: t('CustomerTypeOptions.OccasionalCustomer'),
    regular_customer: t('CustomerTypeOptions.RegularCustomer'),
    strategic_customer: t('CustomerTypeOptions.StrategicCustomer'),
    lost_customer: t('CustomerTypeOptions.LostCustomer'),
  };
  return labels[key] || '';
}

function typeCodeFromRow(row) {
  const mode = String(row?.party_mode || '').trim();
  return String(
    mode === 'client'
      ? (row?.client_type || row?.customer_type || '')
      : mode === 'customer'
        ? (row?.customer_type || row?.client_type || '')
        : (row?.customer_type || row?.client_type || '')
  ).trim();
}

function partnerTypeLabelsFromRow(row, t) {
  const labels = [];
  const types = Array.isArray(row?.types) ? row.types : [];
  if (types.includes('supplier')) labels.push(t('BusinessPartnerAdd.Type.Supplier'));
  if (types.includes('calibration_company')) labels.push(t('BusinessPartnerAdd.Type.CalibrationCompany'));
  if (types.includes('training_company')) labels.push(t('BusinessPartnerAdd.Type.TrainingCompany'));
  if (types.includes('service_company')) labels.push(t('BusinessPartnerAdd.Type.ServiceCompany'));
  const partyMode = String(row?.party_mode ?? '');
  if (partyMode === 'customer') labels.push(t('BusinessPartnerAdd.Type.Customer'));
  if (partyMode === 'client') labels.push(t('BusinessPartnerAdd.Type.Client'));
  return labels.join(', ');
}

function partnerTypeCodesFromRow(row) {
  const out = [];
  const types = Array.isArray(row?.types) ? row.types : [];
  if (types.includes('supplier')) out.push('supplier');
  if (types.includes('calibration_company')) out.push('calibration_company');
  if (types.includes('training_company')) out.push('training_company');
  if (types.includes('service_company')) out.push('service_company');
  const partyMode = String(row?.party_mode ?? '');
  if (partyMode === 'customer') out.push('customer');
  if (partyMode === 'client') out.push('client');
  return out;
}

function parseCountryLabel(countryCode, locale) {
  if (!countryCode) return '';
  const lang = locale === 'de' ? 'de' : (locale === 'en' || locale === 'gb' ? 'en' : 'sk');
  return countries.getName(countryCode, lang) || countryCode;
}

export default function CustomerMyCustomersList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const { toast, openFavoriteDialog, setFastView, setUserMaskFilter, setStandardMaskFilter, openChosenNotificationPreferences, getDateFormat, formatDate, getUserInitials, getUserColor } = useContext(UtilContext);
  const tableRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const isRestoringRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });
  const submittedSearchValueRef = useRef('');
  const [editIcon, setEditIcon] = useState(true);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [loading, setLoading] = useState(false);
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const canEdit = !!user?.permissions?.includes('access customer_management');
  const canSetStandardMask = !!user?.permissions?.includes('set standard mask');
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const deadlineWarningDays = useMemo(() => {
    const raw = (state.module_settings || []).find((s) => s.module === 'crm' && s.key === 'customer_deadline_warning_days')?.value;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 5;
  }, [state.module_settings]);

  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === PAGE_LIST_KEY);
    if (!row) return false;
    const attributes = safeParse(row.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const savedCols = safeParse(row.columns, {});
    const merged = mergeVisibility(savedCols && typeof savedCols === 'object' ? savedCols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const getStandardMaskFallback = useCallback(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === PAGE_LIST_KEY);
    if (!row) return null;
    const attributes = safeParse(row.attributes, []);
    const jsonColumns = safeParse(row.columns, {});
    const merged = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    if (!Object.values(merged).some(Boolean)) return null;
    return { columns: merged, attributes };
  }, [state.standard_mask_filters]);

  const normalizeText = useCallback((value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(), []);
  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    const needle = normalizeText(headerValue);
    if (!needle) return true;
    return normalizeText(rowValue).includes(needle);
  }, [normalizeText]);

  const dateCellText = useCallback((value) => {
    if (!value) return '';
    const dt = DateTime.fromISO(String(value));
    if (!dt.isValid) return String(value);
    return dt.toFormat(dateTimeFmt);
  }, [dateTimeFmt]);

  const deadlineCellText = useCallback((value) => {
    if (!value) return '';
    const dt = DateTime.fromISO(String(value));
    if (!dt.isValid) return String(value);
    return dt.toFormat(dateFmt);
  }, [dateFmt]);

  const deadlineStatus = useCallback((value) => {
    if (!value) return '';
    const deadline = DateTime.fromISO(String(value)).startOf('day');
    if (!deadline.isValid) return '';
    const today = DateTime.local().startOf('day');
    const diff = Math.floor(deadline.diff(today, 'days').days);
    if (diff < 0) return 'red';
    if (diff <= deadlineWarningDays) return 'orange';
    return 'green';
  }, [deadlineWarningDays]);

  const dateOrUserFilter = useCallback((headerValue, _rowValue, rowData, filterParams) => {
    if (!headerValue || String(headerValue).trim() === '') return true;
    const raw = String(headerValue).trim();
    const nameKey = filterParams?.name;
    const dateField = nameKey === 'waiting_for_action_by' ? 'deadline' : (nameKey === 'trader_assigned_from' ? 'trader_assigned_from' : nameKey);
    if (raw.startsWith('range:')) {
      const [, fromRaw = '', toRaw = ''] = raw.match(/^range:([^:]+):([^:]+)$/) || [];
      if (!fromRaw || !toRaw) return true;
      const from = DateTime.fromFormat(fromRaw, 'yyyy-MM-dd').startOf('day');
      const to = DateTime.fromFormat(toRaw, 'yyyy-MM-dd').endOf('day');
      if (!from.isValid || !to.isValid) return true;
      const dt = DateTime.fromISO(String(rowData?.[dateField] || ''));
      if (!dt.isValid) return false;
      return dt >= from && dt <= to;
    }
    const needle = normalizeText(raw);
    let userObj;
    if (nameKey === 'created_at') userObj = rowData?.created_by_user;
    else if (nameKey === 'updated_at') userObj = rowData?.last_touched_by_user;
    else if (nameKey === 'trader_assigned_from') userObj = rowData?.contractor;
    else if (nameKey === 'deadline' || nameKey === 'waiting_for_action_by') userObj = rowData?.execution_user;
    else userObj = rowData?.last_touched_by_user;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[dateField] || ''));
    if (!dt.isValid) return normalizeText(uname).includes(needle);
    return normalizeText(`${uname} ${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}`).includes(needle);
  }, [dateFmt, dateTimeFmt, normalizeText]);

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find(s => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [state.module_settings]);

  const countryFilterOptions = useMemo(() => {
    const lang = locale === 'de' ? 'de' : (locale === 'en' || locale === 'gb' ? 'en' : 'sk');
    const names = countries.getNames(lang) || {};
    return Object.entries(names)
      .map(([code, label]) => ({ value: String(code), label: String(label) }))
      .sort((a, b) => a.label.localeCompare(b.label, locale || undefined));
  }, [locale]);

  const getTable = useCallback(() => tableRef.current?.table ?? tableRef.current, []);
  const refreshFromStart = useCallback(() => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingData')), [getTable, t]);

  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: CUSTOMER_MY_CUSTOMERS_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    const apply = () => {
      holder.scrollLeft = left;
      if (header) header.scrollLeft = left;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(apply, 50);
        setTimeout(() => {
          if (savedHScrollRef.current.pending) {
            apply();
            savedHScrollRef.current.pending = false;
          }
        }, 180);
      });
    });
  }, [getTable]);

  const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
    const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
    if (extra.size == null) extra.size = pageSizePc;
    mergePagedStandardListFiltersAndSorters(extra, {
      getTable,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: PAGE_LIST_KEY,
    });
    const normalizedSearch = String(submittedSearchValueRef.current ?? '').trim();
    if (normalizedSearch !== '') extra.toolbar_search = normalizedSearch;
    return extra;
  }, [getTable, pageSizePc, state.user_mask_filters]);

  const getSelectedRows = useCallback(() => {
    const table = tableRef.current?.table ?? tableRef.current;
    return table?.getSelectedData?.() ?? [];
  }, []);

  const onSelectRow = useCallback((data) => {
    const count = Array.isArray(data) ? data.length : 0;
    setEditIcon(!(canEdit && count === 1));
  }, [canEdit]);

  function openAdd(row = null) {
    if (!row?.id) return;
    dispatch({
      type: 'create_new_tab', name: t('CustomerMyCustomersDetail.Title'), translationKey: 'CustomerMyCustomersDetail.Title', legacyLabel: t('CustomerMyCustomersDetail.Title'),
      page: PAGE_ADD_KEY,
      props: { customerId: row.id, customerRow: row, type: 'edit' },
      mask: MASK_ADD,
    });
  }

  const applyToolbarSearch = useCallback((value) => {
    const normalized = String(value ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    captureHScroll();
    refreshFromStart();
  }, [captureHScroll, refreshFromStart]);

  const searchTable = useCallback(() => {
    applyToolbarSearch(searchValue);
    setSearchIconColor(String(searchValue || '').trim() ? 'active' : '');
    setVisibleSearch(false);
  }, [applyToolbarSearch, searchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setSearchIconColor('');
    applyToolbarSearch('');
  }, [applyToolbarSearch]);

  const buildExportSuffix = useCallback((opts = {}) => {
    const table = getTable();
    const extra = {};
    mergePagedStandardListFiltersAndSorters(extra, {
      getTable,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: PAGE_LIST_KEY,
    });
    const printColumns = (table?.getColumns?.() ?? [])
      .map((col) => {
        const def = col?.getDefinition?.() ?? {};
        return { field: col?.getField?.() ?? def.field, def };
      })
      .filter(({ field, def }) => Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, field) && columnsVisible[field] !== false && def.print !== false && def.download !== false)
      .map(({ field }) => field);
    return buildExportQueryStringFromParams({
      ...extra,
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(table?.getSorters?.() ?? [])),
    }, {
      includePrintColumns: true,
      printColumns,
      ...opts,
    });
  }, [columnsVisible, getTable, state.user_mask_filters]);

  const runPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      const suffix = buildExportSuffix({
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
      });
      const html = await fetchTextGet(`/api/customer_evaluations/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('Common.EnablePopups'), life: 3500 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('ServiceTask.PrintFailed'), life: 4000 });
    }
  }, [buildExportSuffix, printLayoutChoice, printOrientationChoice, t, toast]);

  const runExport = useCallback(async () => {
    try {
      const suffix = buildExportSuffix();
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.CRM_CENTRAL_DB));
      const { blob, filename } = await downloadBlobGet(`/api/customer_evaluations/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Export'), detail: e?.message || t('ServiceTask.ExportFailed'), life: 4000 });
    }
  }, [buildExportSuffix, t, toast]);

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();

    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => openAdd(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function CreatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const raw = rowData?.created_at;
    const u = rowData?.created_by_user;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : dateCellText(raw);
    return (
      <div className="flex items-center gap-2 h-full">
        {u ? <Avatar className="shrink-0" title={u?.employee?.name || u?.username} label={getUserInitials(u?.employee?.name || u?.username)} shape="circle" style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }} /> : null}
        <span>{text}</span>
      </div>
    );
  }

  function UpdatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const raw = rowData?.updated_at;
    const u = rowData?.last_touched_by_user;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : dateCellText(raw);
    return (
      <div className="flex items-center gap-2 h-full">
        {u ? <Avatar className="shrink-0" title={u?.employee?.name || u?.username} label={getUserInitials(u?.employee?.name || u?.username)} shape="circle" style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }} /> : null}
        <span>{text}</span>
      </div>
    );
  }

  function TraderSinceCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.contractor;
    const raw = rowData?.trader_assigned_from;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : dateCellText(raw);
    return (
      <div className="flex items-center gap-2 h-full">
        {u ? <Avatar className="shrink-0" title={u?.employee?.name || u?.username} label={getUserInitials(u?.employee?.name || u?.username)} shape="circle" style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }} /> : null}
        <span>{text}</span>
      </div>
    );
  }

  function DeadlineStatusCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const status = deadlineStatus(rowData?.deadline);
    const className = status === 'red'
      ? 'bg-red-500'
      : status === 'orange'
        ? 'bg-orange-400'
        : status === 'green'
          ? 'bg-green-500'
          : 'bg-gray-300';
    return (
      <div className="flex items-center justify-center w-full h-full">
        <span className={`inline-block w-3 h-3 rounded-full ${className}`} />
      </div>
    );
  }

  function DeadlineCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const raw = rowData?.deadline;
    const deadline = raw ? DateTime.fromISO(String(raw)).startOf('day') : null;
    const isExpired = deadline?.isValid && deadline < DateTime.local().startOf('day');
    const text = formatDate ? formatDate(raw, dateFmt, true) : deadlineCellText(raw);
    const u = rowData?.execution_user;
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 flex-wrap gap-1">
        {u ? <Avatar className="shrink-0" title={u?.employee?.name || u?.username} label={getUserInitials(u?.employee?.name || u?.username)} shape="circle" style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff', marginRight: '5px' }} /> : null}
        {text ? (
          <div className={isExpired ? 'text-red-500 font-semibold shrink-0' : 'shrink-0'} title={text || undefined}>
            {text}
          </div>
        ) : null}
      </div>
    );
  }

  function InterestedUsersFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const rawMentions = Array.isArray(rowData?.mentions) ? rowData.mentions : [];
    const seenIds = new Set();
    const mentions = rawMentions.filter((u) => {
      const id = Number(u?.id);
      if (!Number.isFinite(id) || id <= 0 || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
    if (mentions.length === 0) return null;

    const maxVisible = 3;
    const visibleUsers = mentions.slice(0, maxVisible);
    const extraUsers = mentions.slice(maxVisible);
    const extraCount = extraUsers.length;
    const allNames = mentions
      .map((u) => u?.employee?.name || u?.username)
      .filter(Boolean);
    const extraNames = extraUsers
      .map((u) => u?.employee?.name || u?.username)
      .filter(Boolean);

    return (
      <div className="flex items-center justify-start w-full h-full min-w-0" title={allNames.join('\n') || undefined}>
        <AvatarGroup>
          {visibleUsers.map((u) => {
            const name = u?.employee?.name || u?.username || '';
            return (
              <Avatar
                key={u?.id ?? name}
                title={name}
                label={getUserInitials(name)}
                shape="circle"
                style={{
                  backgroundColor: getUserColor(u?.username),
                  color: '#ffffff',
                  marginRight: '5px',
                }}
              />
            );
          })}
          {extraCount > 0 ? (
            <Avatar
              label={`+${extraCount}`}
              shape="circle"
              title={extraNames.join('\n')}
              style={{
                backgroundColor: '#6b7280',
                color: '#ffffff',
                marginRight: '5px',
              }}
            />
          ) : null}
        </AvatarGroup>
      </div>
    );
  }

  const columns = useMemo(() => ([
    { formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', headerSort: false, frozen: true, width: 45, resizable: false },
    { formatter: reactFormatter(<EditRowFormatter />), width: 55, hozAlign: 'center', headerSort: false, frozen: true, resizable: false, title: '' },
    {
      title: '',
      field: 'deadline_status',
      frozen: true,
      formatter: reactFormatter(<DeadlineStatusCellFormatter />),
      width: 44,
      minWidth: 44,
      hozAlign: 'center',
      headerSort: false,
      resizable: false,
      print: false,
      download: false,
      headerFilter: 'list',
      headerFilterParams: { values: { '': t('Common.All'), red: '🔴', orange: '🟠', green: '🟢' }, clearable: true },
      headerFilterFunc: (headerValue, _rowValue, rowData) => {
        if (!headerValue) return true;
        return deadlineStatus(rowData?.deadline) === headerValue;
      },
    },
    { title: t('ServiceTaskTable.Col.Id'), field: 'id', sorter: 'number', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 0.6, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    { title: t('CustomerMyCustomersList.Col.Name'), field: 'name', sorter: 'string', minWidth: tabulatorColumnMinWidthFromTitle(t('CustomerMyCustomersList.Col.Name')), widthGrow: 1.3, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    {
      title: t('CustomerCentralDatabaseList.Col.State'),
      field: 'customer_state',
      sorter: 'string',
      formatter: (cell) => {
        const val = cell.getValue();
        return val === 'occupied' ? t('CustomerCentralDatabase.State.Occupied') : t('CustomerCentralDatabase.State.Free');
      },
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterParams: {
        placeholder: t('CustomerCentralDatabaseList.Col.State'),
        valueType: 'string',
        options: [
          { label: t('CustomerCentralDatabase.State.Free'), value: 'free' },
          { label: t('CustomerCentralDatabase.State.Occupied'), value: 'occupied' },
        ],
      },
      headerFilterFunc: (headerValue, _rowValue, rowData) => {
        if (!headerValue || String(headerValue).trim() === '') return true;
        const selected = String(headerValue).split(',').map((s) => s.trim()).filter(Boolean);
        if (!selected.length) return true;
        return selected.includes(String(rowData?.customer_state || ''));
      },
      minWidth: tabulatorColumnMinWidthFromTitle(t('CustomerCentralDatabaseList.Col.State'), 'multiselect'),
      widthGrow: 0.8,
      hozAlign: 'left',
    },
    {
      title: t('CustomerMyCustomersList.Col.CustomerClientType'),
      field: 'customer_type',
      sorter: 'string',
      formatter: (cell) => typeLabelFromRow(cell.getRow?.().getData?.() || {}, t),
      minWidth: tabulatorColumnMinWidthFromTitle(t('CustomerMyCustomersList.Col.CustomerClientType')),
      widthGrow: 1,
      hozAlign: 'left',
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterParams: {
        placeholder: t('CustomerMyCustomersList.Col.CustomerClientType'),
        valueType: 'string',
        options: [
          { label: t('CompanySettings.PrintSection.empty'), value: '__empty__' },
          { label: t('CustomerTypeOptions.NewClient'), value: 'new_client' },
          { label: t('CustomerTypeOptions.PotentialClient'), value: 'potential_client' },
          { label: t('CustomerTypeOptions.VerifiedClient'), value: 'verified_client' },
          { label: t('CustomerTypeOptions.InquiringClient'), value: 'inquiring_client' },
          { label: t('CustomerTypeOptions.OccasionalCustomer'), value: 'occasional_customer' },
          { label: t('CustomerTypeOptions.RegularCustomer'), value: 'regular_customer' },
          { label: t('CustomerTypeOptions.StrategicCustomer'), value: 'strategic_customer' },
          { label: t('CustomerTypeOptions.LostCustomer'), value: 'lost_customer' },
        ],
      },
      headerFilterFunc: (headerValue, _rowValue, rowData) => {
        if (!headerValue || String(headerValue).trim() === '') return true;
        const selected = String(headerValue).split(',').map((s) => s.trim()).filter(Boolean);
        if (!selected.length) return true;
        const rowCode = typeCodeFromRow(rowData);
        if (rowCode === '' && selected.includes('__empty__')) return true;
        return rowCode !== '' && selected.includes(rowCode);
      },
    },
    {
      title: t('CustomerCentralDatabaseList.Col.PartnerType'),
      field: 'party_mode',
      sorter: 'string',
      formatter: (cell) => partnerTypeLabelsFromRow(cell.getRow?.().getData?.() || {}, t),
      minWidth: tabulatorColumnMinWidthFromTitle(t('CustomerCentralDatabaseList.Col.PartnerType')),
      widthGrow: 0.8,
      hozAlign: 'left',
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterParams: {
        placeholder: t('CustomerCentralDatabaseList.Col.PartnerType'),
        valueType: 'string',
        options: [
          { label: t('BusinessPartnerAdd.Type.Supplier'), value: 'supplier' },
          { label: t('BusinessPartnerAdd.Type.CalibrationCompany'), value: 'calibration_company' },
          { label: t('BusinessPartnerAdd.Type.TrainingCompany'), value: 'training_company' },
          { label: t('BusinessPartnerAdd.Type.ServiceCompany'), value: 'service_company' },
          { label: t('BusinessPartnerAdd.Type.Customer'), value: 'customer' },
          { label: t('BusinessPartnerAdd.Type.Client'), value: 'client' },
        ],
      },
      headerFilterFunc: (headerValue, _rowValue, rowData) => {
        if (!headerValue || String(headerValue).trim() === '') return true;
        const selected = String(headerValue).split(',').map((s) => s.trim()).filter(Boolean);
        if (!selected.length) return true;
        const rowCodes = partnerTypeCodesFromRow(rowData);
        return selected.some((v) => rowCodes.includes(v));
      },
    },
    { title: t('CustomerCentralDatabaseList.Col.DefinedId'), field: 'defined_id', sorter: 'string', minWidth: tabulatorColumnMinWidthFromTitle(t('CustomerCentralDatabaseList.Col.DefinedId')), widthGrow: 0.8, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    {
      title: t('BusinessPartnerList.Column.Country'),
      field: 'country',
      sorter: 'string',
      minWidth: tabulatorColumnMinWidthFromTitle(t('BusinessPartnerList.Column.Country'), 'multiselect'),
      widthGrow: 1,
      formatter: (cell) => parseCountryLabel(cell.getValue(), locale),
      hozAlign: 'left',
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterParams: {
        placeholder: t('BusinessPartnerList.Column.Country'),
        valueType: 'string',
        options: countryFilterOptions,
      },
      headerFilterFunc: (headerValue, _rowValue, rowData) => {
        if (!headerValue || String(headerValue).trim() === '') return true;
        const selected = String(headerValue).split(',').map((s) => s.trim()).filter(Boolean);
        if (!selected.length) return true;
        return selected.includes(String(rowData?.country || ''));
      },
    },
    { title: t('BusinessPartnerList.Column.Address'), field: 'address', sorter: 'string', minWidth: tabulatorColumnMinWidthFromTitle(t('BusinessPartnerList.Column.Address')), widthGrow: 1.2, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    { title: t('BusinessPartnerList.Column.Email'), field: 'email', sorter: 'string', minWidth: tabulatorColumnMinWidthFromTitle(t('BusinessPartnerList.Column.Email')), widthGrow: 1, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    { title: t('CustomerCentralDatabaseList.Col.Trader'), field: 'trader_name', sorter: 'string', formatter: reactFormatter(<TraderSinceCellFormatter />), minWidth: tabulatorDateUserColumnMinWidth(t('CustomerStatesList.Col.Created')), widthGrow: 1, hozAlign: 'left', headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'trader_assigned_from' } },
    { title: t('CustomerMyCustomersList.Col.InterestedUsers'), field: 'interested_users', sorter: 'string', formatter: reactFormatter(<InterestedUsersFormatter />), minWidth: 140, width: 150, widthGrow: 0.35, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    { title: t('ServiceTaskTable.Col.Deadline'), field: 'waiting_for_action_by', sorter: 'datetime', sorterParams: { format: 'iso' }, formatter: reactFormatter(<DeadlineCellFormatter />), minWidth: tabulatorDateUserColumnMinWidth(t('ServiceTaskTable.Col.Deadline')), widthGrow: 1.3, hozAlign: 'left', headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'waiting_for_action_by' } },
    { title: t('CustomerStatesList.Col.Created'), field: 'created_at', sorter: 'datetime', sorterParams: { format: 'iso' }, formatter: reactFormatter(<CreatedAtCellFormatter />), minWidth: tabulatorDateUserColumnMinWidth(t('CustomerStatesList.Col.Created')), widthGrow: 1, hozAlign: 'left', headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' } },
    { title: t('ServiceTaskTable.Col.Updated'), field: 'updated_at', sorter: 'datetime', sorterParams: { format: 'iso' }, formatter: reactFormatter(<UpdatedAtCellFormatter />), minWidth: tabulatorDateUserColumnMinWidth(t('ServiceTaskTable.Col.Updated')), widthGrow: 1, hozAlign: 'left', headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' } },
  ]), [countryFilterOptions, dateCellText, dateFmt, dateOrUserFilter, dateTimeFmt, deadlineCellText, deadlineStatus, formatDate, getUserColor, getUserInitials, locale, t, textHeaderFilter]);

  const closeMenu = () => {
    setExpandedKeys({});
  };

  const setMask = (field, value) => {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, field)) return;
    const table = tableRef.current?.table ?? tableRef.current;
    const next = { ...DEFAULT_COLUMNS_VISIBLE, ...columnsVisible, [field]: value };
    if (!Object.values(next).some(Boolean)) return;
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  };

  const maskSelectAll = () => {
    const next = { ...DEFAULT_COLUMNS_VISIBLE };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(tableRef.current?.table ?? tableRef.current, next);
    setColumnsVisible(next);
  };

  const maskDeselectAll = () => {
    const next = { ...DEFAULT_COLUMNS_VISIBLE };
    Object.keys(next).forEach((k) => { next[k] = false; });
    next.id = true;
    applyDataColumnVisibilityToTable(tableRef.current?.table ?? tableRef.current, next);
    setColumnsVisible(next);
  };

  const saveUserMaskColumns = () => {
    const table = tableRef.current?.table ?? tableRef.current;
    const filtersSnapshot = table?.getHeaderFilters?.() ?? [];
    const attributes = table?.getColumnLayout?.() ?? null;
    setUserMaskFilter(PAGE_LIST_KEY, 'all', columnsVisible, filtersSnapshot, attributes, null);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('Common.SaveStandardMaskLayoutPrompt'),
      header: t('Common.StandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) {
          toast?.current?.show({ severity: 'warn', summary: t('Common.Mask'), detail: t('Common.TableNotReady'), life: 4000 });
          return;
        }
        const colsForSave = mergeVisibility(layout.columns || {});
        if (!Object.values(colsForSave).some(Boolean)) {
          toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('ServiceTask.AtLeastOneColumnVisible'), life: 4000 });
          return;
        }
        setStandardMaskFilter(PAGE_LIST_KEY, colsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === PAGE_LIST_KEY);
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('BusinessPartnerList.StandardMaskMissing'), life: 4000 });
      return;
    }
    const attributes = safeParse(standard.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) {
      toast?.current?.show({
        severity: 'warn',
        summary: t('Common.StandardMask'),
        detail: t('BusinessPartnerList.StandardMaskMissing'),
        life: 5000,
      });
      return;
    }
    const savedCols = safeParse(standard.columns, {});
    const colsForSave = mergeVisibility(savedCols && typeof savedCols === 'object' ? savedCols : {});
    if (!Object.values(colsForSave).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.StandardMask'), detail: t('BusinessPartnerList.StandardMaskMissing'), life: 4000 });
      return;
    }
    setUserMaskFilter(PAGE_LIST_KEY, 'all', colsForSave, null, attributes, null, () => {
      const table = getTable();
      if (!table) return;
      isRestoringRef.current = true;
      try { table.setColumnLayout?.(attributes); } catch (_) {}
      setColumnsVisible(colsForSave);
      applyDataColumnVisibilityToTable(table, colsForSave);
      setTimeout(() => {
        isRestoringRef.current = false;
        try { table.redraw?.(true); } catch (_) {}
        captureHScroll();
        refreshFromStart();
      }, 0);
    });
  };

  const resetMask = async () => {
    closeMenu();
    await resetTabulatorMask({
      page: PAGE_LIST_KEY,
      defaultColumnsVisible: DEFAULT_COLUMNS_VISIBLE,
      ref: tableRef,
      columns,
      setColumnsVisible,
      getFallbackMask: getStandardMaskFallback,
      progressiveRefresh: {
        getTable,
        completedCacheRef,
        savedHScrollRef,
        loadingMessage: t('Common.LoadingData'),
        beforeRefresh: captureHScroll,
      },
    });
  };

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = [{
    key: '0',
    label: t('ServiceTask.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('ServiceTask.SetFilter'), icon: 'pi pi-filter', command: () => { const table = getTable(); setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, table?.getHeaderFilters?.() ?? [], null); captureHScroll(); refreshFromStart(); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('ServiceTask.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { const table = getTable(); setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, [], null); table?.clearHeaderFilter?.(); captureHScroll(); refreshFromStart(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('ServiceTask.SetSort'), icon: 'pi pi-sort-alt', command: () => { const table = getTable(); setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, cleanSorters(table?.getSorters?.() ?? [])); captureHScroll(); refreshFromStart(); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { const table = getTable(); setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, []); table?.setSort?.([]); captureHScroll(); refreshFromStart(); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); closeMenu(); }, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
      { key: '0_5r', label: t('Common.ResetUserMask'), icon: 'pi pi-refresh', command: () => { resetMask(); }, template: itemRenderer },
      { key: '0_6', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences?.(PAGE_LIST_KEY); closeMenu(); }, template: itemRenderer },
    ],
  }];

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <IntersectionObserverWrapper>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button className="disabled:opacity-25" onClick={() => { const selected = getSelectedRows(); if (selected.length) openAdd(selected[0]); }} disabled={editIcon}><EditIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
          <button title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor === 'active' ? clearSearch : () => setVisibleSearch(true)}>
            {searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />}
          </button>
        </div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Common.Export')} onClick={runExport}><ExportIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Common.Refresh')} onClick={() => { captureHScroll(); refreshFromStart(); }}><RefreshIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Icon.First')} onClick={() => { const table = getTable(); const first = table?.getRows?.()?.[0]?.getData?.(); if (first?.id != null) table?.scrollToRow?.(first.id, 'top', false); }}><BackToFirstIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Icon.Previous')} onClick={() => { const table = tableRef.current?.table ?? tableRef.current; table?.navigatePrev?.(); }}><BackIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Icon.Next')} onClick={() => { const table = tableRef.current?.table ?? tableRef.current; table?.navigateNext?.(); }}><ForwardIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Icon.Last')} onClick={() => { const table = getTable(); const loaded = table?.getRows?.() ?? []; const last = loaded[loaded.length - 1]?.getData?.(); if (last?.id != null) table?.scrollToRow?.(last.id, 'bottom', false); }}><ForwardToLastIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Common.AddToFavorites')} onClick={() => openFavoriteDialog?.(t('CustomerMyCustomersList.Title'), PAGE_LIST_KEY)}><FavoriteIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Common.QuickView')} onClick={() => setFastView?.(PAGE_LIST_KEY)}><PriceIcon /></button></div>
        {canSetStandardMask ? (
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
            <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
              <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
            </button>
          </div>
        ) : null}
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="card flex justify-content-center z-[100]" data-targetid="actions">
          <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
        </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        {loading ? (
          <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-200">
            {t('Common.LoadingData')}
          </div>
        ) : null}
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)] min-w-0">
          <div className="overflow-hidden min-w-0">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                onRef={(r) => (tableRef.current = r?.current)}
                columns={columns}
                className="h-[calc(100vh_-_193px)]"
                selectable
                selectableRows
                rowSelectionChanged={onSelectRow}
                placeholder={loading ? t('Common.LoadingData') : t('CustomerCentralDatabaseList.NoRows')}
                options={{
                  movableColumns: true,
                  resizableRows: false,
                  layout: 'fitColumns',
                  autoResize: true,
                  renderHorizontal: 'virtual',
                  rowHeight: 42,
                  headerSortTristate: true,
                  ajaxURL: '/api/customer_evaluations/paged?mine_only=1',
                  ajaxParams: () => ({}),
                  ajaxRequestFunc: createDedupedAjaxRequest(buildRequestParams, tabulatorGet, inFlightRef, completedCacheRef),
                  dataLoader: true,
                  dataLoaderLoading: t('Common.LoadingData'),
                  dataLoaderError: t('Common.LoadFailed'),
                  progressiveLoad: 'scroll',
                  progressiveLoadScrollMargin: 80,
                  paginationSize: pageSizePc,
                  filterMode: 'remote',
                  sortMode: 'remote',
                  pagination: false,
                }}
                events={{
                  tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask(PAGE_LIST_KEY, state);
                    if (effective?.type === 'user' && effective.userMask) {
                      isRestoringRef.current = true;
                      const m = effective.userMask;
                      applyUserMaskRowToTabulator(table, m, {
                        setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                        clearHeaderFiltersWhenEmpty: true,
                        applyColumnVisibilityFn: (tbl, jc, setVis) => {
                          const merged = mergeVisibility(jc);
                          setVis(merged);
                          applyDataColumnVisibilityToTable(tbl, merged);
                        },
                      });
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                      isRestoringRef.current = true;
                      const attributes = safeParse(effective.standardMask.attributes, []);
                      const jsonColumns = safeParse(effective.standardMask.columns, {});
                      if (Array.isArray(attributes) && attributes.length > 1) {
                        try { table.setColumnLayout?.(attributes); } catch (_) {}
                      }
                      if (jsonColumns && typeof jsonColumns === 'object') {
                        const merged = mergeVisibility(jsonColumns);
                        setColumnsVisible(merged);
                        applyDataColumnVisibilityToTable(table, merged);
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else {
                      applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE);
                    }
                    tableBuiltRef.current = true;
                    setTimeout(() => refreshFromStart(), 0);
                  },
                  ...getTabulatorPersistLayoutEvents(PAGE_LIST_KEY, tableRef, setUserMaskFilter),
                  headerFilterChanged: () => { if (!isRestoringRef.current) captureHScroll(); },
                  sortChanged: () => { if (!isRestoringRef.current) captureHScroll(); },
                  renderComplete: () => restoreHScroll(),
                  ajaxRequesting: () => {
                    captureHScroll();
                    setLoading(true);
                    const table = getTable();
                    if (table?.initialized) table.alert?.(t('Common.LoadingData'));
                    return true;
                  },
                  ajaxResponse: (url, params, response) => {
                    setLoading(false);
                    getTable()?.clearAlert?.();
                    return response;
                  },
                  ajaxError: () => {
                    setLoading(false);
                    getTable()?.clearAlert?.();
                    toast?.current?.show({
                      severity: 'error',
                      summary: t('Common.LoadFailed'),
                      detail: t('CustomerCentralDatabaseList.NoRows'),
                      life: 4000,
                    });
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex items-center gap-3 w-full">
          <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400" />
          <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center"><SearchIcon /></button>
        </div>
      </Dialog>
      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('Common.SetMask')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveUserMaskColumns}
        selectAllLabel={t('Common.SelectAll')}
        deselectAllLabel={t('Common.DeselectAll')}
        saveLabel={t('Common.Save')}
        getFieldLabel={(field) => {
          if (field === 'deadline_status') return t('CustomerBusinessStatusPanel.Semaphore');
          const col = columns.find((item) => item?.field === field);
          return col?.title || field;
        }}
        inputIdPrefix="crm-my-customers-mask"
      />
      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runPrint}
      />
    </div>
  );
}
